/**
 * Create page map et al.
 * @author Matt Haynes, Ana chan
 * @package Dunfermline
 */
import mapboxgl from 'mapbox-gl';
import UpdateContent from './UpdateContent';
import CompleteTour from './CompleteTour';
import Layers from './MapboxLayers.json';

const MapBox = (tourData, pageData, initialCurrentDisplay = 0) => {
    const toggleContent = document.getElementById('toggle_content');
    const contentSection = document.querySelector('.content-section');
    // eslint-disable-next-line
    const buttonsArr = Array.from(
        document.querySelectorAll('.buttons__button')
    );
    const initialInfo = document.querySelector('.initial-information');
    const continueTour = document.querySelector('.continue-tour');
    const tourContent = document.querySelector('.tour-content');
    // eslint-disable-next-line
    const controlButtons = Array.from(
        document.querySelectorAll('.tour_content__contentButton')
    );
    const startTour = document.getElementById('start_tour');
    const mapContainer = document.getElementById('map');
    const visitedButton = document.getElementById('visited');
    const spotifyPlayer = document.getElementById('spotify-player');
    const audioContainer = document.getElementById('audio-container');
    const audioPlayer = document.getElementById('audio-player');

    mapboxgl.accessToken =
        'pk.eyJ1IjoiZHVuZmVybWxpbmV0b3VycyIsImEiOiJjazd1Y3RtZmcwMGI2M2RtbWY3cWoyMTdvIn0.mkAy-z6opZVqOMYcxp-hQA';

    const defaultStyle = 'mapbox://styles/mapbox/streets-v8';

    const map = new mapboxgl.Map({
        container: 'map', // container id
        style: defaultStyle, // stylesheet location
        center:
            pageData.acf.mapbox_lat && pageData.acf.mapbox_lang
                ? [
                      Number(pageData.acf.mapbox_lat),
                      Number(pageData.acf.mapbox_lang),
                  ]
                : [-3.461989, 56.070303], // starting position
        zoom: pageData.acf.mapbox_zoom ? Number(pageData.acf.mapbox_zoom) : 15, // starting zoom
        trackResize: true,
    });

    map.addControl(
        new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true,
            },
            trackUserLocation: true,
            showUserLocation: true,
        })
    );

    const enableMapControls = () => {
        map.scrollZoom.enable();
        map.boxZoom.enable();
        map.dragRotate.enable();
        map.dragPan.enable();
        map.keyboard.enable();
        map.doubleClickZoom.enable();
        map.touchZoomRotate.enable();
    };

    // error handling - use to create redirect/message if Map box tiles do not load
    const error = document.getElementById('error');
    map.on('error', (e) => {
        console.log(e.error, '<======');
        error.style.display = 'flex';
    });

    const customStyle = (tour) => {
        let coordinates = [];
        tour.acf.mapbox_image_coordinates.forEach((coordinate) => {
            coordinates = [
                ...coordinates,
                [
                    parseFloat(coordinate.latitude),
                    parseFloat(coordinate.longitude),
                ],
            ];
        });

        return {
            version: 8,
            name: 'Custom',
            sources: {
                composite: {
                    url:
                        'mapbox://mapbox.mapbox-streets-v8,mapbox.mapbox-terrain-v2',
                    type: 'vector',
                },
                overlay: {
                    type: 'image',
                    url: tour.acf.mapbox_image,
                    coordinates: coordinates,
                },
            },
            sprite: 'mapbox://sprites/mapbox/basic-v9',
            glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
            layers: [
                ...Layers.layers,
                {
                    id: 'overlay',
                    source: 'overlay',
                    type: 'raster',
                    paint: { 'raster-opacity': 1 },
                },
            ],
        };
    };

    const geoJsonObjects = tourData.map((tour) => ({
        type: 'Feature',
        properties: {
            title: 'Mapbox marker',
            iconSize: [36, 48],
            description: tour.title.rendered,
            icon: 'marker',
            url: tour.link,
        },
        geometry: {
            type: 'Point',
            coordinates:
                tour.acf.mapbox_lat && tour.acf.mapbox_lang
                    ? [
                          Number(tour.acf.mapbox_lat),
                          Number(tour.acf.mapbox_lang),
                      ]
                    : [-3.461989, 56.070303],
            style: tour.acf.mapbox_image ? customStyle(tour) : defaultStyle,
        },
    }));

    // geojson data for markers
    const geojson = {
        type: 'FeatureCollection',
        features: [...geoJsonObjects],
    };

    let currentDisplay = initialCurrentDisplay;
    // add markers to map
    geojson.features.forEach((marker, index) => {
        // create a HTML element for each feature
        const el = document.createElement('div');
        el.className = `marker marker_${index}`;
        el.style.width = `${marker.properties.iconSize[0]}px`;
        el.style.height = `${marker.properties.iconSize[1]}px`;
        el.markerIndex = index;
        const elCount = document.createElement('p');
        elCount.innerHTML = index + 1;
        elCount.markerIndex = index;
        el.appendChild(elCount);

        el.addEventListener('click', (e) => {
            if (
                !continueTour.classList.contains('hide') &&
                !initialInfo.classList.contains('hide') &&
                tourContent.classList.contains('hide')
            ) {
                tourContent.classList.remove('hide');
                // disableMapControls();
            }
            if (!continueTour.classList.contains('hide'))
                continueTour.classList.add('hide');
            if (!initialInfo.classList.contains('hide'))
                initialInfo.classList.add('hide');
            if (!contentSection.classList.contains('tour-started'))
                contentSection.classList.add('tour-started');
            if (toggleContent.classList.contains('hide'))
                toggleContent.classList.remove('hide');
            if (spotifyPlayer && spotifyPlayer.classList.contains('hide'))
                spotifyPlayer.classList.remove('hide');
            if (visitedButton.classList.contains('hide'))
                visitedButton.classList.remove('hide');
            if (audioContainer && audioPlayer.src.length > 0) {
                audioContainer.classList.remove('hide');
                audioContainer.classList.remove('init-hide');
            }
            controlButtons.forEach((button) => button.classList.remove('hide'));

            currentDisplay = e.target.markerIndex;

            UpdateContent(tourData, pageData, tourContent, currentDisplay);
        });

        // make a marker for each feature and add to the map
        new mapboxgl.Marker(el)
            .setLngLat(marker.geometry.coordinates)
            .addTo(map);

        el.addEventListener('click', () => {
            map.flyTo({ center: marker.geometry.coordinates });
            map.setStyle(marker.geometry.style);
        });
    });

    const tourName = tourData[0].type;

    const loadMarker = (showStyle = false) => {
        map.flyTo({
            center: geojson.features[currentDisplay].geometry.coordinates,
        });
        if (showStyle) {
            map.setStyle(geojson.features[currentDisplay].geometry.style);
        }
          
    };

    map.on('load', () => {
        enableMapControls();
        startTour.innerHTML = 'Start the tour';
        startTour.disabled = false;

        // Set active Marker
        // eslint-disable-next-line
        const markerList = Array.from(document.querySelectorAll('.marker'));
        markerList.forEach((marker) => {
            if (marker.classList.contains(`marker_${currentDisplay}`)) {
                marker.classList.add('active');
            } else {
                marker.classList.remove('active');
            }
        });
        const visitedOnLoad =
            localStorage.getItem(`visitedItems-${tourName}`) &&
            localStorage.getItem(`visitedItems-${tourName}`).split(',');
        // eslint-disable-next-line compat/compat
        const markerArr = Array.from(document.querySelectorAll('.marker'));

        if (visitedOnLoad && visitedOnLoad.length > 0) {
            visitedOnLoad.forEach((visitedNumber) => {
                // eslint-disable-next-line no-unused-expressions
                markerArr &&
                    markerArr[Number(visitedNumber)].classList.add('visited');
            });
        }

        loadMarker(true);
    });

    const initVisited = localStorage.getItem(`visitedItems-${tourName}`);
    const visited = (initVisited && initVisited.split(',')) || [];
    const visNum = visited.map((vis) => Number(vis));
    visitedButton.addEventListener('click', () => {
        // eslint-disable-next-line
        const markerArr = Array.from(document.querySelectorAll('.marker'));
        if (visitedButton.classList.contains('visited')) {
            markerArr[currentDisplay].classList.remove('visited');
            visitedButton.classList.remove('visited');
            const index = visNum.indexOf(currentDisplay);

            if (index > -1) {
                visNum.splice(index, 1);
            }
            localStorage.setItem(`visitedItems-${tourName}`, visNum);
        } else {
            markerArr[currentDisplay].classList.add('visited');
            visitedButton.classList.add('visited');
            visNum.push(currentDisplay);
            localStorage.setItem(`visitedItems-${tourName}`, visNum);
            CompleteTour(tourData);
        }
    });

    const nextTour = Array.from(document.querySelectorAll('.next-tour'));
    const prevTour = Array.from(document.querySelectorAll('.prev-tour'));
    nextTour.forEach((elem) => {
        elem.addEventListener('click', () => {
            currentDisplay += 1;
            UpdateContent(tourData, pageData, tourContent, currentDisplay);
            loadMarker(true);
            window.scrollTo(0,0);
        });
    });

    prevTour.forEach((elem) => {
        elem.addEventListener('click', () => {
            currentDisplay -= 1;
            UpdateContent(tourData, pageData, tourContent, currentDisplay);
            loadMarker(true);
            window.scrollTo(0,0);
        });
    });

    document.addEventListener('keyup', (e) => {
        // Next
        if (e.key === 'ArrowRight' && currentDisplay !== tourData.length - 1) {
            currentDisplay += 1;
            UpdateContent(tourData, pageData, tourContent, currentDisplay);
            loadMarker(true);
        }

        // Previous
        if (e.key === 'ArrowLeft' && currentDisplay !== 0) {
            currentDisplay -= 1;
            UpdateContent(tourData, pageData, tourContent, currentDisplay);
            loadMarker(true);
        }
    });

    toggleContent.addEventListener('click', () => {
        if (contentSection.classList.contains('toggled')) {
            // enableMapControls();
            map.resize();
            mapContainer.style.overflow = 'visible';
        } else {
            // disableMapControls();
            setTimeout(() => {
                map.resize();
                mapContainer.style.overflow = 'hidden';
            }, 1000);
        }
    });

    startTour.addEventListener('click', () => {
        // enableMapControls();
        map.resize();
    });

    continueTour.addEventListener('click', () => {
        // disableMapControls();
        setTimeout(() => {
            map.resize();
            mapContainer.style.overflow = 'hidden';
        }, 1000);

        loadMarker(true);
    });
};

export default MapBox;
